import React, {useContext, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {TableColumn, TableSettings} from "../../../tableSettings";
import AbstractListView, {
  DataFetchResult,ExportRowRenderer,ListCustomFilterRenderer,ListOrderBy,ListRowRenderer
} from "../../common/AbstractListView";
import {oceanImportLoadPlanList,} from './tableSettings';
import {Search} from "../../../filtering";
import {NavigationType, openNewPage} from '../../../util/navigationHelpers';
import {
  DateRenderer,DateTimeRenderer,FloatRenderer,NullableCheckboxIconRenderer,
  TextAlignLeftRenderer,TextAlignRightRenderer, TextRenderer
} from '../../../uiControls/SmartTableRenderers';
import UniqueLinkButton from '../../../uiControls/UniqueLinkButton';
import useGlobalYesNoDialog from '../../../hooks/useGlobalYesNoDialog';
import ApplicationContext from '../../../ApplicationContext';
import JobListViewContext, { RightClickContext } from '../../common/JobListViewContext';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import useFunctionKeys from '../../../hooks/useFunctionKeys';
import {JobNames} from '../../../framework.core/JobNames';
import {OiLoadListItem} from '../../../fms.data/models/OiLoadListItem';
import * as XCol from '../../../framework.core/XExportColumn';
import ListViewShortcutMenu from '../../common/ListViewShortcutMenu';
import { ActionButtonItem, borderColors, splitMenuItemsIntoSections } from '../../../ui/ActionButton';
import ToolboxMenuItem from '../../../uiControls/ToolboxMenuItem';
import Menu, { IfmsMenuItem } from '../../../ui/menu/Menu';
import { FmsSecTask } from '../../../fms.core/constants';
import InfoAreaDrawer from '../../../uiControls/infoArea/InfoAreaDrawer';
import { TrailerLoadArgs } from '../../../fms.report/args';
import { DocumentType } from '../../../framework.core/DocumentType';
import useReportViewer from '../../../hooks/useReportViewer';
import { findSecurityTask } from '../../../util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { DeleteIconButton, DeleteIconButton2, EditIconButton, EditIconButton2, PrintIconButton, ViewIconButton, ViewIconButton2 } from '../../../ui/IconButton';

function useSelectedLoadPlanOperations() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const history = useHistory();

  const selectedElement = jobListContext.selectedElement as OiLoadListItem;

  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  const { viewReport } = useReportViewer();

  async function create() {
    if (jobListContext.hasOpenedScreenStartingWithUrl('/oceanImport/loadPlan/add')) {
      await showGlobalOkDialog(<p>New operation is already in progress.</p>);
      return;
    }

    openNewPage(history, '/oceanImport/loadPlan/add', NavigationType.newPage);
  }

  async function deleteLoadPlan() {
    if (!selectedElement) {
      return;
    }

    if (
      jobListContext.hasOpenedScreenStartingWithUrl(`/oceanImport/loadPlan/edit/${selectedElement.id}`) || 
      jobListContext.hasOpenedScreenStartingWithUrl(`/oceanImport/loadPlan/view/${selectedElement.id}`)
    ) {
      await showGlobalOkDialog('Need to close Load Plan first', false, null, "warning");
      return;
    }

    const doDelete = await showGlobalYesNoDialog(
      <div>
        <h3>Do you want to Delete?</h3>
      </div>
    ,false, 0, true, 'warning');

    if (doDelete) {
      await appContext.api.deleteJobDetail(JobNames.OITruckLoad, { id:selectedElement.id, pimsLinkId: 0 });
      jobListContext.reloadData();
    }
  }

  async function edit() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;
    const editUrl = `/oceanImport/loadPlan/edit/${id}`;
    const viewUrl = `/oceanImport/loadPlan/view/${id}`;

    // if (jobListContext.hasOpenedScreenStartingWithUrlWithoutMe('/oceanImport/loadPlan/edit', editUrl)) {
    //   await showGlobalOkDialog(<p>Edit operation is already in progress.</p>);
    //   return;
    // }

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(editUrl, [editUrl, viewUrl]);
  }

  async function view() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;

    const editUrl = `/oceanImport/loadPlan/edit/${id}`;
    const viewUrl = `/oceanImport/loadPlan/view/${id}`;
    const listSec = findSecurityTask(appContext, FmsSecTask.OITruckLoad);
    const urls = [viewUrl];
    if (listSec?.grantEdit) urls.push(editUrl)

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(viewUrl, urls);
  }

  async function print(){
    const title = `Express Trailer Load Plan [${selectedElement.loadNo}]`;
    const args = new TrailerLoadArgs({
      docType: DocumentType.TrailerLoad,
      subject: title,
      formCaption:title,
      tbId: selectedElement.id.toString(),
      toCompanyid:`${selectedElement.truckingCoId}${String.fromCharCode(127)}${selectedElement.warehouseId}`,
      tbName: 'T_TLOADHD'
    })
    viewReport(args, "", true);
  }


  return { create, deleteLoadPlan, edit, view, print };
}

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const { create, deleteLoadPlan, edit, view } = useSelectedLoadPlanOperations();
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.OITruckLoad);

  const items = [
    { label: 'New', shortcut: 'F2', separator: false, newCol: false, hidden: !listSec?.grantAdd  },
    { label: 'View', shortcut: 'F9', separator: !listSec?.grantEdit && !listSec?.grantDel },
    { label: 'Edit', shortcut: 'F3', separator: !listSec?.grantDel, hidden: !listSec?.grantEdit  },
    { label: 'Delete', shortcut: '', separator: true, hidden: !listSec?.grantDel },
    { label: 'Search', shortcut: 'F5', separator: true },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem name="oiLoadPlan-list-sub-menu" width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    newCol: i.newCol,
    index,
    hidden: i?.hidden ?? false,
  }));

  function onMenuItemSelected(item:IfmsMenuItem) {
    switch (item.index) {
      case 0:
        create();
        break;
      case 1:
        view();
        break;
      case 2:
        edit();
        break;
      case 3:
        deleteLoadPlan();
        break;
      case 4:
        jobListContext.triggerTableSearch('F5');
        break;
      case 5:
        jobListContext.reloadData();
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}

function LoadPlanListCommands() {
  const jobListContext = useContext(JobListViewContext);

  const selectedElement = jobListContext.selectedElement as OiLoadListItem;
  const appContext = useContext(ApplicationContext);

  const { deleteLoadPlan, edit, view, print } = useSelectedLoadPlanOperations();

  const listSec = findSecurityTask(appContext, FmsSecTask.OITruckLoad);
  
  const hasSelectedRow = !!selectedElement;
  const canBeDeleted = hasSelectedRow && listSec?.grantDel;
  const canBeAdd = listSec?.grantAdd;
  const canBeEdit = hasSelectedRow && listSec?.grantEdit;

  return (
    <div className='ifms-common-no-vertical-separator-gap-margin'>
      <div className="ifms-common-vertical-gap-8">  
        {/* {appContext.suntekSettings?.company?.billingsTabVersion === 2 ?  <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}/> : <></>} */}
        <UniqueLinkButton newStyle name='oiLoadPlan-list-add-button' display="primary" link="/oceanImport/loadPlan/add" disabled={!canBeAdd} label={<FontAwesomeIcon icon={faPlus} />} useOnlyIcon data-tooltip='New' iconMode />
        <EditIconButton2 name='oiLoadPlan-list-edit-button' disabled={!canBeEdit} onClick={edit} useText/>
        <ViewIconButton2 name='oiLoadPlan-list-view-button' disabled={!hasSelectedRow} onClick={view} useText />
        <DeleteIconButton2 name='oiLoadPlan-list-delete-button' disabled={!canBeDeleted} onClick={deleteLoadPlan} useText/>
      </div>
      <div className="ifms-common-bar-element-center">  
        <PrintIconButton name='oiLoadPlan-list-print-button' disabled={!hasSelectedRow} onClick={print} tooltip='Print Load Plan' useText/>
      </div>
    </div>

  );
}

function LoadPlanListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function formatData(data:Array<OiLoadListItem>) {
  return data;
}

function LoadPlanListViewExtras({
  hideLogHistory
}: any,
) {
  useFunctionKeys({onFunction: onFunctionKey});

  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.OITruckLoad);

  const { create, edit, view } = useSelectedLoadPlanOperations();

  const [unprocessedFkey, setUnprocessedFkey] = useState<string|null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === "F2") {
      if(listSec?.grantAdd)
        create();
    } else if (unprocessedFkey === "F3") {
      if(listSec?.grantEdit)
        edit();
    } else if (unprocessedFkey === "F9") {
      view();
    } else if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    }

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (['F2', 'F3', 'F9', 'F11'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }


  
  return (
    <InfoAreaDrawer
      hideMemoList={true}
      showContent={jobListContext.infoAreaContent}
      jobName={JobNames.OILoad}
      jobId={(jobListContext.selectedElement as OiLoadListItem|null)?.id ?? 0}
      onClose={() => jobListContext.setInfoAreaContent('none')}
    />
  );
}

/**
 * Concrete implementation for Load Plan List screen
 *
 * @class LoadPlanListView
 * @extends {AbstractListView}
 */
 class LoadPlanListView extends AbstractListView {

  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(JobNames.OILoad, 'OiLoadListItem',
    OiLoadListItem.calculateSearchParameters(search, OiLoadListItem.MapSearchFields),
    pageNum,
    limit,
    search.pageSize,
    search.pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return { data: formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
    const apiResult = await api.getListItems(JobNames.OILoad, 'OiLoadListItem',
      OiLoadListItem.calculateSearchParameters(search, OiLoadListItem.MapSearchFields, []),
      pageNum,
      limit,
      pageSize,
      pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return formatData(apiResult.entities);
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
    ];
  }

  getInfoAreaEntityId() {
    return '';
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status'] === 'PC' ? 'Period Close' : ''}});
    return colRenderer;
  }

  getRowRenderers(): ListRowRenderer[] {
    return OiLoadListItem.getExportCollection().map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      if(x.align === XCol.XAlignType.Right) colRenderer.Cell = TextAlignRightRenderer;
      if(x.dataIndex === "status") {
        colRenderer.Cell = () => <span></span>;
        colRenderer.Header = () => <span></span>;
      }
      if(x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      else if(x.type === XCol.XCellType.Double) colRenderer.Cell = FloatRenderer;
      return colRenderer;
    });
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'createdAt',
      orderByAscending: false,
    };
  }

  getTableSettings(): TableSettings {
    const tsettings = oceanImportLoadPlanList;
    const cols = OiLoadListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });

    return tsettings;
  }

  renderCommands() {
    return (
      <LoadPlanListCommands />
    );
  }

  renderBeforeConfigureButton() {
    return (
      <LoadPlanListToolbar />
    );
  }


  renderExtras(hideLogHistory: boolean) {
    return <LoadPlanListViewExtras hideLogHistory={hideLogHistory} />
  }

  getViewUrlFor(row: OiLoadListItem) {
    return row && row.id ? `/oceanImport/loadPlan/view/${row.id}` : '';
  }

  getListViewTitle() {
    return 'Load Plan List';
  };

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }
  
  useLogHistory(): boolean {
    return true;
  }
};

export default LoadPlanListView;
